/**
 * A casca: liga rede, controle e render. É o ÚNICO arquivo do cliente que conhece
 * `window`, `document` e `localStorage` — mesma disciplina do `main.ts` do servidor
 * com `process.env`.
 *
 * Nada de regra de jogo aqui. Esta função existe para montar as peças e sair da frente.
 */
import { tokens } from '@game/design';
import { failLimit, INVARIANT_BUDGETS } from '@game/gamedata/perf';
import type { MessageData } from '@game/protocol';
import { balance, roomFor } from '@game/gamedata';
import { ACTION_MAGNET, bodyKindFromCode, type BodyKind } from '@game/sim';
import { MATCH_END_NO_CODE } from '@game/telemetry';
import { matchEndView, type MatchEndKind } from './ui/match-end.js';
import { buildInfo, buildLine, buildStamp, type BuildInfo } from './build-info.js';
import { createRecorder } from './telemetry/recorder.js';
import { markRetry, readSession } from './telemetry/session.js';
import { installDebugApi, type DebugBody, type DebugPlayer } from './debug-api.js';
import { isEnabled, loadFlags, snapshotFlags } from './flags.js';
import { IntentSource } from './input/intent-source.js';
import { loadAvatarGeometry } from './render/avatar.js';
import { loadAvatarRig } from './render/avatar-rig.js';
import { canPredictBody } from './net/body-authority.js';
import { BodyHandoff } from './net/body-handoff.js';
import { connect, type Connection } from './net/connection.js';
import { Interpolator, type InterpolatedBody } from './net/interpolation.js';
import { Predictor } from './net/prediction.js';
import { FrameSampler } from './perf/frame-sampler.js';
import { RenderScaleController, optionsForTier } from './perf/render-scale.js';
import { startingTier, type TierStore } from './perf/tier.js';
import { Bodies, type FieldView } from './render/bodies.js';
import { FrameLoop } from './render/loop.js';
import { createRenderer } from './render/renderer.js';
import { createScene } from './render/scene.js';
import { World } from './render/world.js';
import { SoloSession, SOLO_PLAYER_ID } from './solo/solo.js';
import { HudStore } from './ui/hud-store.js';

/** Porta padrão do servidor de sala. Trocável por `VITE_SERVER_URL`. */
const DEFAULT_SERVER_PORT = 8787;
const GUEST_TOKEN_KEY = 'game.guestToken';

/**
 * Seed da partida solo. Constante de propósito: a sala do protótipo é FIXA, e o
 * que se está medindo é o feel do ímã — sala diferente a cada abertura tornaria
 * duas tentativas incomparáveis, que é o oposto do que o teste com jogador pede.
 */
const SOLO_SEED = 1;

/**
 * Cadência das amostras de quadro e rede da caixa-preta.
 *
 * Alta de propósito: p95 e pior 1% são estatística de JANELA, e amostrar mais
 * rápido NEVER traz mais informação — traz mais linha para o mesmo fato, e banda
 * do jogador é orçamento (`BUD-bandwidth-up`). Cinco segundos casa com o intervalo
 * de envio, então o lote sai com uma amostra dentro em vez de vazio.
 */
const PROBE_SAMPLE_MS = 5000;

/**
 * `localStorage` lança em aba privada e em contexto de terceiro. O jogo NEVER pode
 * morrer por causa disso: sem armazenamento, o jogador vira convidado novo a cada
 * carregamento, e isso é degradação aceitável — tela branca não é.
 */
const storage: TierStore = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Sem persistência é degradação, NEVER erro.
    }
  },
};

/**
 * HUMAN-REVIEW — identidade de convidado.
 * Este token É a conta do jogador até existir login (REV-12): quem tiver o valor,
 * é o jogador. Perder o `localStorage` é perder a conta, e o vínculo
 * convidado→conta ainda NEVER foi escrito. NEVER dê isto como pronto sem revisão.
 */
function guestToken(): string {
  const existing = storage.get(GUEST_TOKEN_KEY);
  if (existing !== null && existing !== '') return existing;
  const fresh = crypto.randomUUID();
  storage.set(GUEST_TOKEN_KEY, fresh);
  return fresh;
}

function serverUrl(): string {
  const configured: unknown = import.meta.env.VITE_SERVER_URL;
  if (typeof configured === 'string' && configured !== '') return configured;
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${location.hostname}:${String(DEFAULT_SERVER_PORT)}`;
}

function roomId(): string {
  return new URLSearchParams(location.search).get('room') ?? 'arena-1';
}

/**
 * Onde a caixa-preta entrega o lote. Sai do MESMO servidor do socket, e NEVER da
 * origem da página: em stg o bundle é servido pelo Caddy e o `/t` mora no
 * container do jogo, e mandar para a origem daria 404 em silêncio.
 */
function telemetryUrl(): string {
  const ws = serverUrl();
  return `${ws.replace(/^ws/, 'http').replace(/\/ws$/, '')}/t`;
}

/**
 * De onde vêm os assets pesados (modelo, textura, áudio). Default: a MESMA origem
 * que serviu a página.
 *
 * Existe configurável para que sair da VPS seja uma variável de ambiente, e NEVER
 * uma mudança de código. Hoje NEVER precisa: MEDIDO, o modelo é 37 KB e a abertura
 * inteira do jogo é ~222 KB, o que dá ~9 milhões de aberturas dentro dos 2 TB
 * gratuitos da VPS. O motivo real de mover um dia NEVER é a conta de egress — é
 * que a máquina tem 2 núcleos e engasga em ~11% dos ticks, e servir estático
 * disputa CPU com o laço da sala.
 *
 * ALWAYS termine em barra: o chamador concatena caminho relativo.
 */
function assetBaseUrl(): string {
  const configured: unknown = import.meta.env.VITE_ASSET_BASE_URL;
  if (typeof configured === 'string' && configured !== '') {
    return configured.endsWith('/') ? configured : `${configured}/`;
  }
  return import.meta.env.BASE_URL;
}

/**
 * O botão do polegar direito. Existe nos dois caminhos desde `ADR-0017`.
 *
 * O elemento já existe no HTML; o que este arquivo faz é vesti-lo com token de
 * `@game/design` (ART-DIRECTION M1) e ligá-lo ao controle. Tamanho bem acima de
 * `touch.minTarget`: 44 px é o mínimo de um botão de UI, e este é segurado com o
 * polegar durante a partida inteira.
 */
function mountMagnetButton(input: IntentSource): void {
  const button = document.getElementById('magnet');
  if (!(button instanceof HTMLElement)) return;

  const size = tokens.touch.minTarget * 2;
  button.style.display = 'block';
  button.style.width = `${String(size)}px`;
  button.style.height = `${String(size)}px`;
  button.style.borderRadius = `${String(tokens.radius.pill)}px`;
  button.style.background = tokens.color.role.accentSurface;
  button.style.color = tokens.color.role.accentText;
  button.style.font = `${String(tokens.type.weight.semibold)} ${String(tokens.type.size.sm)}px ${tokens.type.family.ui}`;
  button.style.letterSpacing = tokens.type.tracking.wide;
  button.style.zIndex = String(tokens.layer.hud);
  input.attachMagnet(button);
}

/**
 * O carimbo de versão no canto inferior esquerdo.
 *
 * DOM puro, e NEVER React: o React só entra no bundle quando `perfHud` liga
 * (`import()` dinâmico lá embaixo, por causa de `BUD-bundle`), e o carimbo tem que
 * aparecer para quem NEVER ligou flag nenhuma — é justamente essa pessoa que
 * precisa saber qual build está na mão dela.
 *
 * Tocar copia a linha completa: no celular NEVER dá para selecionar texto (a
 * página inteira tem `user-select: none` por causa do arrasto), então sem isto o
 * SHA só sairia dali digitado à mão, e SHA digitado à mão vem errado.
 */
function mountBuildStamp(info: BuildInfo): void {
  const element = document.getElementById('build');
  if (!(element instanceof HTMLElement)) return;

  element.textContent = buildStamp(info);
  element.style.display = 'block';
  element.style.zIndex = String(tokens.layer.hud);
  element.style.font = `${String(tokens.type.weight.regular)} ${String(tokens.type.size.xs)}px ${tokens.type.family.numeric}`;
  // Sujo é o único estado que pede ação: o que está no ar NEVER sai do git, então
  // o replay daquela partida NEVER volta a existir a partir de um commit.
  element.style.color = info.dirty
    ? tokens.color.semantic.warn
    : tokens.color.role.textMuted;
  element.style.borderRadius = `${String(tokens.radius.control)}px`;

  element.addEventListener('click', () => {
    const line = buildLine(info);
    // Sem `clipboard` (http em rede local, permissão negada) o carimbo NEVER pode
    // falhar calado: mostrar a linha inteira ainda deixa o testador fotografar.
    void navigator.clipboard?.writeText(line).then(
      () => {
        element.textContent = 'copiado';
        setTimeout(() => (element.textContent = buildStamp(info)), 1200);
      },
      () => {
        element.textContent = line;
      },
    );
  });
}

/**
 * Diz ao jogador que a partida parou, e por quê.
 *
 * Existe porque o cliente ficava MUDO: o servidor encerra em
 * `matchDurationTicks`, avisa com `reject`/`matchOver` e fecha — e o cliente
 * respondia com um `console.warn`. MEDIDO no aparelho real: `tick` parado em
 * 5400 por 100 s, com 74 toques do jogador contra um mundo congelado a 60 fps.
 *
 * DOM burro de propósito, igual ao `.tsx` do HUD: toda decisão de texto mora em
 * `ui/match-end.ts`, que é puro e testado sem browser. E DOM, NEVER React — este
 * painel é o único caminho que TODO jogador atravessa, e fazer o fim da partida
 * depender do bundle do React seria pôr `BUD-bundle` entre o jogador e a
 * única tela que ele NEVER pode deixar de ver.
 */
const MATCH_END_COLOR: Readonly<Record<MatchEndKind, string>> = {
  won: tokens.color.semantic.ok,
  over: tokens.color.role.textPrimary,
  lost: tokens.color.semantic.warn,
  blocked: tokens.color.semantic.warn,
};

function showMatchEnd(reason: string, code: number | undefined): void {
  const host = document.getElementById('match-end');
  if (!(host instanceof HTMLElement)) return;
  // Idempotente: `onClose` do transporte e o do protocolo podem chegar os dois.
  if (host.childElementCount > 0) return;

  const view = matchEndView(reason, code);

  host.style.zIndex = String(tokens.layer.modal);
  // Opaco, NEVER translúcido: o mundo atrás está parado, e mundo parado à mostra
  // é exatamente a leitura de "travou" que este painel existe para desfazer.
  host.style.background = tokens.color.role.canvasBg;
  host.style.gap = `${String(tokens.space[3])}px`;
  host.style.padding = `${String(tokens.space[5])}px`;

  const title = document.createElement('p');
  title.textContent = view.title;
  title.style.margin = '0';
  title.style.font = `${String(tokens.type.weight.semibold)} ${String(tokens.type.size['2xl'])}px ${tokens.type.family.display}`;
  // Três leituras, e NEVER duas: vitória é a única linha VERDE que o jogo tem, e
  // pintá-la da mesma cor do "o tempo acabou" jogaria fora a diferença que o
  // jogador atravessou a sala inteira para conseguir.
  title.style.color = MATCH_END_COLOR[view.kind];

  const detail = document.createElement('p');
  detail.textContent = view.detail;
  detail.style.margin = '0';
  detail.style.font = `${String(tokens.type.weight.regular)} ${String(tokens.type.size.base)}px ${tokens.type.family.ui}`;
  detail.style.color = tokens.color.role.textSecondary;

  host.append(title, detail);

  if (view.canRetry) {
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = 'jogar de novo';
    retry.style.marginTop = `${String(tokens.space[3])}px`;
    retry.style.minHeight = `${String(tokens.touch.minTarget)}px`;
    retry.style.padding = `0 ${String(tokens.space[5])}px`;
    retry.style.border = `1px solid ${tokens.color.role.controlBorder}`;
    retry.style.borderRadius = `${String(tokens.radius.control)}px`;
    retry.style.background = tokens.color.role.accentSurface;
    retry.style.color = tokens.color.role.accentText;
    retry.style.font = `${String(tokens.type.weight.medium)} ${String(tokens.type.size.base)}px ${tokens.type.family.ui}`;
    // Recarregar, NEVER reconectar: reconexão é fatia própria, e a sala antiga
    // NEVER volta. Prometer "de novo" e cair na mesma sala morta seria pior que
    // não oferecer nada.
    retry.addEventListener('click', () => {
      // ANTES do reload, e NEVER depois: a página seguinte é outro programa, e a
      // única memória que atravessa a recarga é a que foi escrita aqui.
      markRetry();
      location.reload();
    });
    host.append(retry);
  }

  host.style.display = 'flex';
}

async function start(): Promise<void> {
  loadFlags();
  const build = buildInfo();
  const canvas = document.getElementById('game');
  if (!(canvas instanceof HTMLCanvasElement)) throw new Error('canvas #game não existe');
  document.body.style.background = tokens.color.role.canvasBg;

  const tier = startingTier(storage, {
    hardwareConcurrency: navigator.hardwareConcurrency,
  });
  /**
   * A caixa-preta (`ADR-0018`). Inerte sem `?t=CÓDIGO`, e o chamador NEVER
   * ramifica: `record` num gravador inerte NEVER faz nada, do mesmo jeito que o
   * gravador de replay do servidor.
   *
   * Nasce cedo para o `session.start` sair antes de qualquer coisa poder falhar:
   * uma sessão que morre na abertura é justamente a que mais precisa dizer em que
   * aparelho ela morreu.
   */
  const probeSession = readSession();
  const probe = createRecorder({
    session: probeSession,
    build,
    endpoint: telemetryUrl(),
  });

  const sampler = new FrameSampler();
  const scale = new RenderScaleController(optionsForTier(tier));
  const bundle = createScene();
  const renderer = createRenderer(canvas);
  // -1 até o `welcome` dizer quem somos: NEVER existe jogador com esse id.
  const world = new World(bundle.scene, -1);
  const bodies = new Bodies(bundle.scene, roomFor(isEnabled('level01')).objective.exit);
  const input = new IntentSource();
  input.attach(canvas);

  /**
   * Partida local do protótipo do ímã. Existir aqui é a bifurcação inteira: com
   * ela, NEVER se conecta; sem ela, o cliente é exatamente o que era.
   */
  const solo = isEnabled('solo') ? new SoloSession(SOLO_SEED) : undefined;
  // O botão existe nos DOIS caminhos desde `ADR-0017`. Ficava só no solo enquanto
  // o campo só existia lá — e o efeito era que, no celular, a partida em rede NEVER
  // tinha como magnetizar: o teclado é do desenvolvedor, o botão é do jogador.
  mountMagnetButton(input);
  // Cedo de propósito: se a partida travar depois daqui, o carimbo já está na tela,
  // e "travou" passa a ser um relato com versão em vez de um relato sem prova.
  if (isEnabled('buildStamp')) mountBuildStamp(build);

  if (solo !== undefined) {
    // Sem isto o avatar da partida solo sai com a cor de OUTRO jogador: quem
    // chama `setSelfId` é o caminho em rede, a partir do `welcome`, e a partida
    // solo NEVER passa por lá. Ficou invisível enquanto o avatar era um cubo
    // cinza entre corpos cinzas; com o modelo na tela, "você" ter a cor de
    // terceiro é a primeira coisa que se vê.
    world.setSelfId(SOLO_PLAYER_ID);
  }

  /** Caixa mutável de propósito: o laço de frame começa antes de existir conexão. */
  const session: {
    connection?: Connection;
    predictor?: Predictor;
    interpolator?: Interpolator;
  } = {};
  let lastTick = 0;
  let lastPlayers: readonly DebugPlayer[] = [];
  let lastBodies: readonly DebugBody[] = [];
  /** Reaproveitado: montar um objeto de feixe por frame seria lixo a 60 Hz. */
  const field = { fromX: 0, fromZ: 0, targetId: 0 };
  /**
   * Os corpos vindos do servidor, já traduzidos para o que o render entende.
   * Reaproveitado pela mesma regra do `view`: o snapshot chega a `BUD-snapshot`,
   * e montar lista nova a cada um é lixo que o `BUD-js-frame` cobra depois.
   */
  const bodyView: {
    id: number;
    kind: BodyKind;
    x: number;
    z: number;
    heldBy: number;
    /** Quantos campos puxam este corpo. É o que decide se o palpite local vale. */
    claims: number;
  }[] = [];
  /** Alvo do campo do PRÓPRIO jogador, do último snapshot. 0 = campo desligado. */
  let selfMagnetTargetId = 0;
  /** O corpo que o ÚLTIMO desenho tirou do palpite. 0 = tudo veio do passado. */
  let predictedBodyDrawn = 0;
  /**
   * O corpo que este cliente pode desenhar pelo palpite local, decidido pelo ÚLTIMO
   * snapshot. 0 = nenhum, e o mundo inteiro vem do passado interpolado.
   *
   * Decidido na chegada do snapshot, e NEVER por frame, porque a decisão é de
   * AUTORIDADE e não de desenho: a posição desenhada é a de um `BUD-interp` atrás, mas
   * "posso confiar no meu palpite?" tem que ser respondida com o que a sala disse por
   * ÚLTIMO. Lendo `claims` do instante desenhado, o corpo levaria um buffer inteiro
   * para começar a responder ao dedo depois de o servidor já ter confirmado o campo —
   * um atraso que a predição do `ADR-0017` existe justamente para não ter.
   */
  let predictableBodyId = 0;
  /**
   * Os corpos do instante desenhado, escritos pelo relógio de interpolação. Vive fora
   * do laço pela mesma regra do `view`: alocar por frame é lixo que `BUD-js-frame` cobra.
   */
  const bodyFrame: InterpolatedBody[] = [];
  /** A costura entre as duas bases de tempo do corpo. Sem estado de jogo: só memória
   *  do que foi desenhado, para o salto da troca virar velocidade. */
  const handoff = new BodyHandoff();

  /**
   * Traduz corpos do fio para o render. O `kind` vem como CÓDIGO — do snapshot ou do
   * interpolador, que o carrega intacto — e a volta pode falhar: servidor mais novo
   * manda tipo que este cliente NEVER conhece. Corpo desconhecido é PULADO, e NEVER
   * desenhado como palpite — um corpo com o tamanho errado é pior que um corpo
   * ausente, porque o jogador tentaria magnetizar o que está vendo.
   */
  const refreshBodies = (
    incoming: MessageData<'snapshot'>['bodies'] | readonly InterpolatedBody[],
  ): void => {
    let count = 0;
    for (const source of incoming) {
      const kind = bodyKindFromCode(source.kind);
      if (kind === undefined) continue;
      const slot = bodyView[count] ?? { id: 0, kind, x: 0, z: 0, heldBy: 0, claims: 0 };
      slot.id = source.id;
      slot.kind = kind;
      slot.x = source.x;
      slot.z = source.z;
      slot.heldBy = source.heldBy;
      slot.claims = source.claims;
      bodyView[count] = slot;
      count += 1;
    }
    bodyView.length = count;
  };

  /**
   * O que vai para o render, montado em duas camadas de tempo diferentes e de
   * propósito: os OUTROS vêm do passado interpolado (`BUD-interp`), e o PRÓPRIO avatar
   * vem do futuro previsto. Cada um no tempo em que dá para acertar — a intenção do
   * dono o cliente tem, a dos outros NEVER.
   *
   * Reaproveitada e mutada no lugar: montar lista nova por frame é lixo que o
   * `BUD-js-frame` cobra.
   */
  const view: {
    id: number;
    x: number;
    z: number;
    hp: number;
    facingX?: number;
    facingZ?: number;
  }[] = [];

  /**
   * A mira é OPCIONAL na origem: o estado do sim tem, o `snapshot` do protocolo
   * NEVER teve. Aceitar as duas formas aqui é o que evita a alternativa ruim —
   * um campo obrigatório que a rede preencheria com zero, e zero é uma direção
   * válida que faria o avatar apontar para o norte a partida inteira.
   */
  const refreshView = (
    players: readonly (DebugPlayer & { readonly facingX?: number; readonly facingZ?: number })[],
  ): void => {
    view.length = players.length;
    for (let i = 0; i < players.length; i++) {
      const source = players[i]!;
      const slot = view[i] ?? { id: 0, x: 0, z: 0, hp: 0 };
      slot.id = source.id;
      slot.x = source.x;
      slot.z = source.z;
      slot.hp = source.hp;
      slot.facingX = source.facingX;
      slot.facingZ = source.facingZ;
      view[i] = slot;
    }
  };

  /**
   * Aplica a decisão já tomada em `predictableBodyId`: o corpo de que este cliente é o
   * único reivindicante passa a ser desenhado pelo palpite local, e todo o resto
   * continua vindo do passado interpolado. É a troca de base de tempo do `ADR-0017`.
   *
   * Devolve o id que REALMENTE saiu do palpite, e NEVER o que poderia ter saído: com a
   * predição desligada, ou antes do primeiro snapshot, o corpo continua vindo do
   * servidor. Quem costura as duas bases precisa saber qual delas desenhou, e uma
   * intenção NEVER é uma base.
   */
  const predictSoleClaimedBody = (predictor: Predictor | undefined): number => {
    if (predictor?.ready !== true || predictableBodyId === 0) return 0;

    for (const slot of bodyView) {
      if (slot.id !== predictableBodyId) continue;
      for (const predicted of predictor.predictedState.bodies) {
        if (predicted.id !== slot.id) continue;
        slot.x = predicted.x;
        slot.z = predicted.z;
        return slot.id;
      }
      break;
    }
    return 0;
  };

  /**
   * Sobrescreve só o próprio avatar com o palpite local, e desenha.
   *
   * `deltaMs` é o tempo desde o desenho anterior, e serve só à costura das bases de
   * tempo do corpo. Zero quando o desenho é disparado pela CHEGADA do snapshot: entre
   * o frame que acabou e ele NEVER passou tempo nenhum.
   */
  const drawWorld = (deltaMs: number): void => {
    const predictor = session.predictor;
    const selfId = session.connection?.welcome.playerId;
    let selfX = 0;
    let selfZ = 0;
    let selfFound = false;
    if (predictor?.ready === true) {
      const self = predictor.selfPosition;
      // A mira vem do palpite pelo mesmo motivo que a posição: quem apontou foi este
      // dedo. Deixá-la sair do interpolador faria o PRÓPRIO robô girar um `BUD-interp`
      // depois do comando, com o feixe saindo torto até o passado alcançar.
      const facing = predictor.selfFacing;
      for (const slot of view) {
        if (slot.id !== selfId) continue;
        slot.x = self.x;
        slot.z = self.z;
        slot.facingX = facing.x;
        slot.facingZ = facing.z;
      }
    }
    for (const slot of view) {
      if (slot.id !== selfId) continue;
      selfX = slot.x;
      selfZ = slot.z;
      selfFound = true;
    }
    world.sync(view);
    // Depois do `sync` porque é da POSIÇÃO recém-escrita que a velocidade sai, e
    // NEVER da do frame anterior.
    world.animate(deltaMs);
    // A costura vem DEPOIS das duas bases terem escrito, porque é a diferença entre
    // elas que ela existe para engolir. Ver `ADR-0017` e `net/body-handoff.ts`.
    predictedBodyDrawn = predictSoleClaimedBody(predictor);
    handoff.apply(bodyView, predictedBodyDrawn, deltaMs);

    // O feixe sai de onde o avatar ESTÁ NA TELA, e NEVER de onde o servidor disse
    // que ele está: com predição ligada os dois divergem pelo RTT, e um feixe
    // saindo do lado do próprio robô é a primeira coisa que se vê.
    if (selfMagnetTargetId === 0 || !selfFound) {
      bodies.sync(bodyView, undefined);
      return;
    }
    field.fromX = selfX;
    field.fromZ = selfZ;
    field.targetId = selfMagnetTargetId;
    bodies.sync(bodyView, field satisfies FieldView);
  };
  /** Desenha a partida local: sem autoridade remota, o estado do sim vai direto. */
  const drawSolo = (): void => {
    if (solo === undefined) return;
    refreshView(solo.players);
    world.sync(view);

    const targetId = solo.magnetTargetId;
    const self = view.find((slot) => slot.id === SOLO_PLAYER_ID);
    if (targetId === 0 || self === undefined) {
      bodies.sync(solo.bodies, undefined);
      return;
    }
    field.fromX = self.x;
    field.fromZ = self.z;
    field.targetId = targetId;
    bodies.sync(solo.bodies, field satisfies FieldView);
  };

  let lastCloseReason: string | undefined;
  let sinceInputMs = 0;
  let sincePingMs = 0;
  let elapsedMs = 0;
  const inputPeriodMs = 1000 / INVARIANT_BUDGETS['BUD-input-rate'].value;
  /**
   * Teto de ticks recuperados por frame. Sem teto, um frame longo viraria rajada de
   * input — `reject` por rateLimited de um lado, avatar teleportando do outro.
   * Aparelho que NEVER sustenta o dobro do período simplesmente manda menos input,
   * que é a degradação que `BUD-input-rate` já prevê.
   */
  const MAX_STEPS_PER_FRAME = 2;
  /**
   * Cadência do ping. Uma vez por segundo é diagnóstico, NEVER gameplay: o RTT
   * que interessa é a tendência, e medir mais vezes só gastaria banda que o
   * `BUD-bandwidth-down` conta.
   */
  const pingPeriodMs = 1000;
  /**
   * Picos que viram telemetria por sessão. `T-DMA` teve 3 acima de 80 ms numa
   * sessão inteira; o teto é folgado de propósito, e existe para o aparelho que
   * pica sem parar NEVER gastar a banda de quem está jogando.
   */
  const MAX_SPIKES_PER_SESSION = 24;

  if (probe.enabled) {
    // O aparelho, uma vez. É o contexto sem o qual todo número depois vira número
    // solto: 22 ms de pior 1% é ótimo num aparelho fraco e péssimo num bom.
    probe.record({
      t: probe.elapsedMs(),
      kind: 'session.start',
      screenW: window.screen.width,
      screenH: window.screen.height,
      dpr: window.devicePixelRatio,
      cores: navigator.hardwareConcurrency,
      tier,
      // Dedo ou mouse. Muda a leitura de TODO evento de toque: `pointercancel`
      // num mouse é outra história que `pointercancel` num polegar.
      coarsePointer: window.matchMedia('(pointer: coarse)').matches,
      // De onde veio esta abertura. `retry` é o Portão 02 inteiro: é a única
      // linha do arquivo que diz que alguém QUIS jogar de novo (`ADR-0023`).
      resumedBy: probeSession.resumedBy,
    });

    /**
     * A pergunta travada no `WIP`: por que o botão do ímã solta sozinho.
     *
     * Até aqui a resposta dependia de o usuário ler `ímã u/c/b` na tela do
     * celular e contar. Isto responde sozinho — e responde melhor, porque traz
     * QUANDO e depois de quanto tempo segurando, que o contador acumulado NEVER
     * tem como dizer.
     */
    input.watchMagnetRelease((cause, heldMs, pointerId, activePointers) => {
      probe.record({
        t: probe.elapsedMs(),
        kind: 'input.magnet_release',
        cause,
        heldMs,
        pointerId,
        activePointers,
      });
    });

    // O PAR da queda. MEDIDO em `T-DXX`: o campo cai em ciclo de ~98 ms preso /
    // ~87 ms solto, e o código bloqueia repetição — então para cada queda existe um
    // `pointerdown` NOVO no botão. É este evento que diz de QUEM ele é.
    input.watchMagnetDown((pointerId, pointerType, activePointers, captured, sinceReleaseMs) => {
      probe.record({
        t: probe.elapsedMs(),
        kind: 'input.magnet_down',
        pointerId,
        pointerType,
        activePointers,
        captured,
        sinceReleaseMs,
      });
    });

    /**
     * A outra pergunta travada: o objeto PULA ao pegar ou soltar (`ADR-0017`).
     * `seamUnits` é o salto que o jogador teria visto sem a mistura — é a medida
     * que falta para `BUD-body-handoff` deixar de ser hipótese de 200 ms.
     */
    handoff.watchSeams((bodyId, seamUnits, toPredicted, windowMs) => {
      probe.record({
        t: probe.elapsedMs(),
        kind: 'body.handoff',
        bodyId,
        from: toPredicted ? 'interp' : 'prev',
        to: toPredicted ? 'prev' : 'interp',
        seamUnits,
        windowMs,
      });
    });

    /**
     * Quadro e rede, por amostra. `setInterval` e NEVER dentro do laço de frame:
     * `compute()` ordena a janela inteira, e pagar isso por frame seria a
     * telemetria virando o custo que ela deveria estar medindo.
     */
    const probeTimer = setInterval(() => {
      const stats = sampler.compute();
      if (stats.count > 0) {
        probe.record({
          t: probe.elapsedMs(),
          kind: 'frame.window',
          frames: stats.count,
          p95Ms: stats.p95Ms,
          worst1PercentMs: stats.worst1PercentMs,
          maxMs: stats.maxMs,
          tier,
          renderScale: scale.scale,
        });
      }
      const open = session.connection;
      const rttMs = open?.rttMs;
      const interp = session.interpolator;
      if (rttMs !== undefined) {
        // O estado do RELÓGIO vai junto do RTT, e é o que a medição de 2026-08-15
        // pediu: com o buffer estourando `BUD-interp-max` em todo aparelho, o que
        // falta saber é se o relógio TENTA se salvar (`snaps` acima de zero) ou se
        // NEVER tenta. Os contadores são acumulados — a diferença entre duas
        // amostras é o que aconteceu na janela.
        const ready = interp?.ready === true;
        probe.record({
          t: probe.elapsedMs(),
          kind: 'net.sample',
          rttMs,
          reconcileUnits: session.predictor?.lastErrorUnits ?? 0,
          interpBufferMs: ready ? interp.bufferMs : 0,
          interpTargetMs: ready ? interp.bufferTargetMs : 0,
          reanchors: ready ? interp.reanchorCount : 0,
          snaps: ready ? interp.snapCount : 0,
          interpRate: ready ? interp.rateNow : 1,
          stale: ready ? interp.staleSnapshots : 0,
        });
      }
    }, PROBE_SAMPLE_MS);

    // O último lote sai daqui. `visibilitychange` e NEVER `unload`: em iOS o
    // `unload` NEVER dispara de forma confiável, e é justamente o celular que
    // interessa. `hidden` cobre trocar de aba, bloquear a tela e fechar.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') probe.flush(true);
    });

    // Em dev, cada HMR reexecuta este módulo. Sem soltar o temporizador sobra um
    // amostrador por edição, e a caixa-preta passa a medir a si mesma.
    import.meta.hot?.dispose(() => {
      clearInterval(probeTimer);
      probe.stop();
    });
  }

  // A coleta é preguiçosa: `compute()` ordena a janela inteira e só roda quando o
  // HUD publica, a ≤ 10 Hz — NEVER por frame.
  const hud = new HudStore(() => ({
    stats: sampler.compute(),
    tier,
    renderScale: scale.scale,
    rttMs: session.connection?.rttMs,
    players: lastPlayers.length,
    // `undefined` com a predição desligada: zero passaria por "palpite perfeito"
    // justamente quando NEVER houve palpite nenhum.
    reconErrorUnits: session.predictor?.lastErrorUnits,
    droppedInputs: session.predictor?.droppedInputs,
    magnetReleases: input.magnetReleases,
    // A base de tempo do corpo e o preço da última troca dela (`ADR-0017`).
    // `undefined` na partida local: sem rede NEVER existem duas bases para trocar.
    bodyBase:
      solo !== undefined || session.connection === undefined
        ? undefined
        : predictedBodyDrawn !== 0
          ? 'prev'
          : 'interp',
    bodySeams: solo !== undefined ? undefined : handoff.seamCount,
    bodySeamUnits: solo !== undefined ? undefined : handoff.lastSeamUnits,
    bodySettlingMs: solo !== undefined ? undefined : handoff.settlingMs,
    // Mesma regra do lado da interpolação: antes do primeiro snapshot o buffer NEVER
    // é zero, é desconhecido — e zero é justamente o valor que grita "congelou".
    interpBufferMs:
      session.interpolator?.ready === true ? session.interpolator.bufferMs : undefined,
    interpTargetMs: session.interpolator?.bufferTargetMs,
    interpReanchors: session.interpolator?.joltCount,
  }));
  let unmountHud: (() => void) | undefined;

  // Instalado antes de conectar de propósito: o E2E precisa poder observar o
  // estado `connecting`, NEVER só o final feliz.
  const uninstallDebug = installDebugApi({
    build: () => build,
    net: () => ({
      status:
        solo !== undefined
          ? 'solo'
          : session.connection === undefined
            ? 'connecting'
            : session.connection.closed
              ? 'closed'
              : 'open',
      matchId: session.connection?.welcome.matchId,
      playerId: session.connection?.welcome.playerId,
      tick: lastTick,
      lastAckSeq: session.connection?.lastAckSeq ?? 0,
      rttMs: session.connection?.rttMs,
      closeReason: lastCloseReason,
    }),
    players: () => lastPlayers,
    bodies: () => lastBodies,
    // Em rede o alvo é AUTORITATIVO, e vem do snapshot; no solo é o estado local.
    // Devolver sempre o do solo fazia este campo mentir zero na partida em rede —
    // e é exatamente ele que o E2E usa para provar que o auto-lock pegou.
    magnetTargetId: () => solo?.magnetTargetId ?? selfMagnetTargetId,
    perf: () => {
      const stats = sampler.compute();
      return {
        frames: stats.count,
        p95Ms: stats.p95Ms,
        worst1PercentMs: stats.worst1PercentMs,
        maxMs: stats.maxMs,
      };
    },
    flags: () => snapshotFlags(),
  });

  const applyViewport = (currentScale: number): void => {
    bundle.setViewport(canvas.clientWidth, canvas.clientHeight);
    renderer.setViewport(canvas.clientWidth, canvas.clientHeight, currentScale);
  };
  applyViewport(scale.scale);
  window.addEventListener('resize', () => applyViewport(scale.scale));

  const loop = new FrameLoop({
    sampler,
    scale,
    draw: (currentScale, deltaMs) => {
      if (solo !== undefined) {
        const move = input.read();
        const advanced = solo.advance(deltaMs, {
          moveX: move.x,
          moveZ: move.z,
          magnet: input.magnet,
        });
        // Frame curto NEVER redesenha um mundo que não mudou.
        if (advanced > 0) {
          lastTick = solo.tick;
          lastPlayers = solo.players;
          lastBodies = solo.bodies;
          drawSolo();
        }
        applyViewport(currentScale);
        renderer.draw(bundle.scene, bundle.camera);
        elapsedMs += deltaMs;
        if (unmountHud !== undefined) hud.tick(elapsedMs);
        return;
      }

      sinceInputMs += deltaMs;
      const open = session.connection;
      let steps = 0;
      if (open !== undefined) {
        // Passo FIXO, e um input enviado é exatamente um tick previsto: é essa
        // igualdade que faz a reexecução casar com o que a sala vai aplicar.
        // Subtrai em vez de zerar — zerar joga fora o resto do frame, e o cliente
        // andaria mais devagar que o servidor em aparelho que NEVER fecha 60 fps.
        while (sinceInputMs >= inputPeriodMs && steps < MAX_STEPS_PER_FRAME) {
          sinceInputMs -= inputPeriodMs;
          steps += 1;
          const move = input.read();
          const predictor = session.predictor;
          // `tick` é o que o cliente ACREDITA estar simulando; o servidor valida.
          // Com predição é o tick previsto; sem ela, o último que ele confirmou.
          const tick = predictor?.ready === true ? predictor.tick : lastTick;
          // O botão do ímã VIAJA. Estava fixo em zero enquanto o campo só existia
          // na partida local: em rede o jogador segurava o botão e NEVER acontecia
          // nada, porque a intenção morria aqui. Ver `ADR-0017`.
          const actions = input.magnet ? ACTION_MAGNET : 0;
          const seq = open.sendIntent({ tick, moveX: move.x, moveZ: move.z, actions });
          // Depois do envio de propósito: a predição é indexada pelo `seq` que o
          // `sendIntent` devolveu, e é por ele que o ack vai casar.
          //
          // A MESMA `actions` dos dois lados, e isso NEVER é detalhe: prever com
          // ação diferente da que foi enviada faz a reexecução divergir do que a
          // sala vai aplicar, e a reconciliação corrige isso como se fosse erro
          // de rede — todo tick, para sempre.
          predictor?.predict({ seq, moveX: move.x, moveZ: move.z, actions });
        }
        // Aba que volta do segundo plano acumula segundos de atraso. Descarta o
        // excedente: mandar a rajada inteira levaria `reject` por rateLimited, e
        // simular tudo de uma vez seria teleporte na tela.
        if (sinceInputMs > inputPeriodMs) sinceInputMs = 0;
      }

      // Os outros andam com o relógio de FRAME, e NEVER com o de input: é isso que os
      // tira da cadência de `BUD-snapshot`. Sem interpolação, a tela só muda quando
      // chega snapshot ou quando o dono mexe — que é o comportamento de antes.
      const interpolator = session.interpolator;
      if (interpolator?.ready === true) {
        interpolator.advance(deltaMs);
        interpolator.sample(view);
        // O corpo do companheiro sai da cadência da rede pela MESMA porta dos outros
        // jogadores, e por um motivo que é o mesmo: a intenção que o move NEVER é
        // deste cliente. O corpo que ele puxa sozinho é sobrescrito logo depois, no
        // `drawWorld`, pelo palpite local.
        interpolator.sampleBodies(bodyFrame);
        refreshBodies(bodyFrame);
        drawWorld(deltaMs);
      } else if (steps > 0) {
        drawWorld(deltaMs);
      }

      sincePingMs += deltaMs;
      if (open !== undefined && sincePingMs >= pingPeriodMs) {
        sincePingMs = 0;
        // Sem isto o `rttMs` da conexão NEVER sai de undefined: o pong é a única
        // medida de ida e volta que o cliente tem.
        open.sendPing();
      }
      applyViewport(currentScale);
      renderer.draw(bundle.scene, bundle.camera);

      // Depois do desenho de propósito: o HUD mede o frame que acabou de sair.
      elapsedMs += deltaMs;
      if (unmountHud !== undefined) hud.tick(elapsedMs);
    },
    // O DOBRO do limite de falha do tier. O limite sozinho pegaria janela
    // apertada demais e encheria o laudo; o dobro pega o que o jogador chama de
    // travada — em `low` são 80 ms, e `T-DMA` mediu 119, 166 e 173.
    spikeMs: failLimit('BUD-frame-time', tier) * 2,
    onSpike: (frameMs, drawMs, index) => {
      // Teto por sessão, e NEVER por janela: um aparelho que pica sem parar
      // gastaria a banda do jogador contando a mesma história. O `index` viaja
      // para o laudo saber que o número é um PISO.
      if (index > MAX_SPIKES_PER_SESSION) return;
      probe.record({
        t: probe.elapsedMs(),
        kind: 'frame.spike',
        frameMs,
        drawMs,
        scale: scale.scale,
        index,
      });
    },
  });
  // Começa antes de conectar: a arena vazia aparece na hora, e o jogador NEVER
  // olha para tela preta enquanto o socket abre.
  loop.start();

  // Depois do laço: a arena tem prioridade sobre a ferramenta de quem desenvolve.
  // `import()` dinâmico NEVER é enfeite — é o que mantém React fora do bundle
  // inicial quando a flag está desligada, que é o caso do jogador (BUD-bundle).
  const hudElement = document.getElementById('hud');
  if (isEnabled('perfHud') && hudElement !== null) {
    const { mountPerfHud } = await import('./ui/mount.js');
    unmountHud = mountPerfHud(hudElement, hud);
  }

  /**
   * O modelo do robô, se a flag pedir. NEVER com `await` na frente: o jogo já
   * está rodando, e um `.glb` em 4G ruim seguraria a partida inteira por vários
   * segundos — `BUD-ttfm` mede TEMPO ATÉ JOGAR, e jogar com cubo é jogar. O
   * avatar troca no frame em que a malha chegar.
   */
  if (isEnabled('robotAnim')) {
    void loadAvatarRig(`${assetBaseUrl()}models/robot-anim.glb`, {
      // Um terço e dois terços da velocidade máxima do jogo. O número que manda
      // é `moveSpeed` (invariante 6); o que está aqui é a FRAÇÃO dele, e ela é
      // sobre leitura de animação, e NEVER sobre balanceamento.
      walk: balance.player.moveSpeed / 3,
      run: (balance.player.moveSpeed * 2) / 3,
    }).then(
      (rig) => {
        world.useAvatarRig(rig);
      },
      (error: unknown) => {
        console.warn('avatar animado não carregou; seguindo com o cubo', error);
      },
    );
  } else if (isEnabled('robot')) {
    void loadAvatarGeometry(`${assetBaseUrl()}models/robot.glb`).then(
      (geometry) => {
        world.useAvatarGeometry(geometry);
      },
      (error: unknown) => {
        // Degradação, NEVER erro: sem o modelo o jogo continua no cubo, que é
        // exatamente o que o jogador com a flag desligada já vê.
        console.warn('modelo do avatar não carregou; seguindo com o cubo', error);
      },
    );
  }

  // Só em dev: cada atualização de HMR reexecuta este módulo. Sem soltar o que
  // ficou para trás sobra um laço de frame e um socket por edição — e o
  // desenvolvedor vê jogador fantasma na sala e culpa o netcode.
  import.meta.hot?.dispose(() => {
    loop.stop();
    uninstallDebug();
    unmountHud?.();
    input.detach();
    session.connection?.close();
    renderer.dispose();
    world.dispose();
    bodies.dispose();
    bundle.dispose();
  });

  if (solo !== undefined) {
    // Partida local: NEVER existe socket, welcome, predição nem interpolação.
    // Um desenho agora para a sala aparecer antes do primeiro toque.
    drawSolo();
    return;
  }

  const opened = await connect({
    url: serverUrl(),
    roomId: roomId(),
    guestToken: guestToken(),
  });
  world.setSelfId(opened.welcome.playerId);
  // O chão da sala só é desenhado depois do `welcome`, e é o SERVIDOR que diz o
  // que tem nele: desenhar a partir de `gamedata` mostraria uma zona que a sala
  // pode ter desligado, e prometer perigo onde NEVER existe é pior que não
  // desenhar nada.
  bodies.setHazards(opened.welcome.hazards);
  // Amarra os próximos eventos à partida. É o `matchId` que liga o que o APARELHO
  // viu ao replay que o SERVIDOR gravou — sem ele, os dois contam histórias
  // separadas sobre o mesmo minuto.
  probe.setMatch(opened.welcome.matchId, opened.welcome.playerId);
  probe.record({
    t: probe.elapsedMs(),
    kind: 'match.join',
    matchId: opened.welcome.matchId,
    playerId: opened.welcome.playerId,
    seats: lastPlayers.length,
  });
  // Depois do `welcome`: `playerId` e `seed` são dele, e sem os dois a predição
  // NEVER saberia quem prever nem com que mundo.
  if (isEnabled('predict')) {
    session.predictor = new Predictor(
      opened.welcome.playerId,
      opened.welcome.seed,
      opened.welcome.hazards,
    );
  }
  if (isEnabled('interp')) session.interpolator = new Interpolator();
  opened.onSnapshot((snapshot) => {
    lastTick = snapshot.tick;
    // Escrita AUTORITATIVA, e é dela que sai o retrato de depuração. Com interpolação
    // ligada, o laço de frame reescreve `bodyView` com o instante desenhado antes de
    // qualquer desenho acontecer — mas `lastBodies` já copiou o que a sala disse, que
    // é o que o E2E tem que poder cobrar. Palpite e passado NEVER entram no contrato.
    refreshBodies(snapshot.bodies);
    lastBodies = bodyView.map((body) => ({ ...body }));
    selfMagnetTargetId =
      snapshot.players.find((player) => player.id === opened.welcome.playerId)?.magnetTargetId ?? 0;
    // A base de tempo do corpo é decidida AQUI, com a autoridade mais nova na mão.
    predictableBodyId = 0;
    for (const body of snapshot.bodies) {
      if (!canPredictBody(body, selfMagnetTargetId)) continue;
      predictableBodyId = body.id;
      break;
    }
    // O contrato de depuração continua AUTORITATIVO: `__GAME__.players` é o que o
    // servidor disse, NEVER o palpite. É o que faz o E2E de convergência significar
    // alguma coisa — dois clientes concordando sobre o próprio palpite NEVER prova nada.
    lastPlayers = snapshot.players;
    session.predictor?.reconcile(snapshot);
    if (session.interpolator === undefined) {
      // Sem interpolação o snapshot vai direto para a tela, aos pulos de `BUD-snapshot`.
      refreshView(snapshot.players);
      drawWorld(0);
      return;
    }
    // Com ela, quem escreve `view` é o `sample` do laço de frame: escrever aqui
    // também seria a tela recebendo dois tempos diferentes no mesmo frame.
    session.interpolator.record(snapshot);
  });
  opened.onClose((reason) => {
    // Reconexão é fatia própria; o mundo congela no último estado visto — e é
    // JUSTAMENTE por isso que o painel abaixo NEVER é opcional: sem ele, mundo
    // congelado é indistinguível de jogo quebrado.
    lastCloseReason = reason;
    console.warn('conexão encerrada', reason);
    if (isEnabled('matchEnd')) showMatchEnd(reason, opened.closeCode);
    probe.record({
      t: probe.elapsedMs(),
      kind: 'match.end',
      matchId: opened.welcome.matchId,
      playerId: opened.welcome.playerId,
      seats: lastPlayers.length,
      // COMO acabou, e NEVER só que acabou. Sem este campo, vitória e tempo
      // esgotado são a mesma linha no lote, e o desfecho só existe no log do
      // servidor. `undefined` é o fio caindo sem decisão da sala.
      code: opened.closeCode ?? MATCH_END_NO_CODE,
    });
    // Manda AGORA, e por beacon: partida que acaba porque o celular perdeu o
    // sinal NEVER tem um "depois" em que o lote periódico ainda vá sair.
    probe.flush(true);
  });
  session.connection = opened;
}

start().catch((error: unknown) => {
  console.error('falha ao iniciar o cliente', error);
});
